import React from 'react';
import { Table, Button, Dropdown, Badge } from 'react-bootstrap';
import './AdminDataTable.css';

export interface TableColumn {
  key: string;
  label: string;
  sortable?: boolean;
  render?: (value: any, row: any) => React.ReactNode;
  width?: string;
}

export interface TableAction {
  label: string;
  variant: 'primary' | 'secondary' | 'success' | 'danger' | 'warning' | 'info' | 'light' | 'dark';
  onClick: (row: any) => void;
  icon?: string;
  show?: (row: any) => boolean;
}

interface AdminDataTableProps {
  data: any[];
  columns: TableColumn[];
  actions?: TableAction[];
  loading?: boolean;
  emptyMessage?: string;
  striped?: boolean;
  hover?: boolean;
  responsive?: boolean;
}

const AdminDataTable: React.FC<AdminDataTableProps> = ({
  data,
  columns,
  actions = [],
  loading = false,
  emptyMessage = "No data available",
  striped = true,
  hover = true,
  responsive = true
}) => {

  const renderCellValue = (column: TableColumn, row: any) => {
    const value = row[column.key];
    
    if (column.render) {
      return column.render(value, row);
    }
    
    return value;
  };

  const renderActions = (row: any) => {
    if (actions.length === 0) return null;

    return (
      <Dropdown align="end">
        <Dropdown.Toggle 
          variant="outline-secondary" 
          size="sm"
          className="kebab-menu-toggle"
          id={`dropdown-${row.id}`}
        >
          <i className="fas fa-ellipsis-v"></i>
        </Dropdown.Toggle>
        <Dropdown.Menu className="kebab-menu">
          {actions.map((action, index) => {
            if (action.show && !action.show(row)) return null;
            
            return (
              <Dropdown.Item
                key={index}
                onClick={() => action.onClick(row)}
                className="kebab-menu-item"
              >
                {action.icon && <i className={`${action.icon} me-2`}></i>}
                {action.label}
              </Dropdown.Item>
            );
          })}
        </Dropdown.Menu>
      </Dropdown>
    );
  };

  if (loading) {
    return (
      <div className="admin-table-loading">
        <div className="text-center py-5">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-data-table">
      <Table striped={striped} hover={hover} responsive={responsive}>
        <thead className="table-dark">
          <tr>
            {columns.map((column) => (
              <th 
                key={column.key} 
                style={{ width: column.width }}
                className={column.sortable ? 'sortable-column' : ''}
              >
                {column.label}
                {column.sortable && (
                  <i className="fas fa-sort ms-1 text-muted"></i>
                )}
              </th>
            ))}
            {actions.length > 0 && (
              <th style={{width: "150px"}} className="text-center">Actions</th>
            )}
          </tr>
        </thead>
        <tbody>
          {data.length === 0 ? (
            <tr>
              <td colSpan={columns.length + (actions.length > 0 ? 1 : 0)} className="text-center py-4">
                <div className="empty-state">
                  <i className="fas fa-inbox fa-2x text-muted mb-2"></i>
                  <p className="text-muted">{emptyMessage}</p>
                </div>
              </td>
            </tr>
          ) : (
            data.map((row, rowIndex) => (
              <tr key={row.id || rowIndex}>
                {columns.map((column) => (
                  <td key={column.key} className="align-middle">
                    {renderCellValue(column, row)}
                  </td>
                ))}
                {actions.length > 0 && (
                  <td className="text-center align-middle">
                    {renderActions(row)}
                  </td>
                )}
              </tr>
            ))
          )}
        </tbody>
      </Table>
    </div>
  );
};

export const createStatusBadge = (status: string) => {
  const variant = status === 'active' ? 'success' : status === 'pending' ? 'warning' : 'danger';
  return <Badge bg={variant}>{status.toUpperCase()}</Badge>;
};

//TODO: USE ENUMS
export const createDifficultyBadge = (difficulty: string) => {
  const variants: Record<string, string> = {
    'Easy': 'success',
    'Moderate': 'warning', 
    'Challenging': 'danger',
    'Expert': 'dark'
  };
  return <Badge bg={variants[difficulty] || 'secondary'}>{difficulty}</Badge>;
};

export const formatElevationGain = (elevationGain?: number) => {
  if (!elevationGain || elevationGain === 0) return 'N/A';
  return `${elevationGain} m`;
};

export const truncateText = (text: string, maxLength: number = 50) => {
  if (!text) return '';
  return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
};

export const formatDate = (date: string | Date) => {
  if (!date) return '';
  return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

export default AdminDataTable; 